
import { ArtItem, Language } from '../types';
import { Image as ImageIcon, Zap, Maximize2, Flower, Loader2, CreditCard, Star } from 'lucide-react';
import { MASTER_FALLBACK_IMAGE } from '../constants';
import React, { useState, useEffect, useRef } from 'react';
import { slugify } from '../utils';

interface ArtCardProps {
  item: ArtItem;
  onAddToCart: (item: ArtItem, signature?: string) => void;
  onPreview: (item: ArtItem) => void;
  language: Language;
  discountPercentage?: number;
}

const getAspectClass = (item: ArtItem) => {
  if (item.aspectRatio === 'horizontal') return 'aspect-video';
  if (item.aspectRatio === 'square') return 'aspect-square';
  if (item.aspectRatio === 'vertical') return 'aspect-[9/14]';

  // Fallback to auto-detection for older items
  const categoryLower = (item.category || '').toLowerCase();
  const subcategoryLower = (item.subcategory || '').toLowerCase();
  const artTypeLower = (item.artType || '').toLowerCase();
  const titlePtLower = (item.title?.pt || '').toLowerCase();
  const titleEnLower = (item.title?.en || '').toLowerCase();

  if (
    categoryLower.includes('avatar') || 
    artTypeLower.includes('avatar') || 
    subcategoryLower.includes('avatar') ||
    titlePtLower.includes('avatar') ||
    titleEnLower.includes('avatar')
  ) {
    return 'aspect-square';
  }

  if (
    categoryLower.includes('banner') ||
    categoryLower.includes('wallpaper') ||
    categoryLower.includes('background') ||
    subcategoryLower.includes('banner') ||
    subcategoryLower.includes('wallpaper') ||
    subcategoryLower.includes('background') ||
    artTypeLower.includes('banner') ||
    titlePtLower.includes('banner') ||
    titleEnLower.includes('banner') ||
    titlePtLower.includes('header') ||
    titleEnLower.includes('header') ||
    titlePtLower.includes('fundo') ||
    titleEnLower.includes('background')
  ) {
    return 'aspect-video';
  }

  return 'aspect-[9/14]';
};

const ArtCard: React.FC<ArtCardProps> = ({ item, onAddToCart, onPreview, language, discountPercentage = 0 }) => {
  const hasDiscount = discountPercentage > 0;
  const finalPrice = hasDiscount ? item.price * (1 - discountPercentage / 100) : item.price;
  const [imgError, setImgError] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [isNearScreen, setIsNearScreen] = useState(false); // Carrega imagens/pre-fetch
  const [isExactlyVisible, setIsExactlyVisible] = useState(false); // Ativa Vídeos/Animações
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Individual 5-Star Rating states and deterministic initial setup
  const [userRating, setUserRating] = useState<number | null>(null);
  const [hoverStar, setHoverStar] = useState<number | null>(null);
  const [showSavedMsg, setShowSavedMsg] = useState(false);

  const initialRating = React.useMemo(() => {
    let hash = 0;
    const idStr = item.id || '';
    for (let i = 0; i < idStr.length; i++) {
      hash = idStr.charCodeAt(i) + ((hash << 5) - hash);
    }
    const count = Math.abs(hash % 15) + 14; // 14 to 28 review counts
    const rawAvg = 4.6 + (Math.abs(hash % 4) * 0.1); // 4.6, 4.7, 4.8, 4.9
    const average = Math.min(5.0, Math.max(4.0, rawAvg));
    return { average, count };
  }, [item.id]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('niiko_user_art_ratings');
      if (stored) {
        const ratingsMap = JSON.parse(stored);
        if (ratingsMap[item.id]) {
          setUserRating(ratingsMap[item.id]);
        }
      }
    } catch (e) {
      console.warn('Error loading ratings', e);
    }
  }, [item.id]);

  const handleRate = (ratingValue: number) => {
    setUserRating(ratingValue);
    setShowSavedMsg(true);
    setTimeout(() => {
      setShowSavedMsg(false);
    }, 2500);

    try {
      const stored = localStorage.getItem('niiko_user_art_ratings');
      const ratingsMap = stored ? JSON.parse(stored) : {};
      ratingsMap[item.id] = ratingValue;
      localStorage.setItem('niiko_user_art_ratings', JSON.stringify(ratingsMap));
    } catch (e) {
      console.warn('Error saving rating', e);
    }
  };

  const displayCount = userRating !== null ? initialRating.count + 1 : initialRating.count;
  const displayAverage = userRating !== null 
    ? ((initialRating.average * initialRating.count) + userRating) / displayCount 
    : initialRating.average;

  useEffect(() => {
    // Observer mais agressivo para economia de recursos
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          // Desmonta recursos pesados se estiver longe
          if (entry.isIntersecting) {
            setIsNearScreen(true);
            setIsExactlyVisible(true);
          } else {
            setIsExactlyVisible(false);
            // Mantém isNearScreen true se já foi visto para evitar "piscada" ao rolar de volta, 
            // mas removemos o vídeo no DOM abaixo baseado em isExactlyVisible.
          }
        });
      },
      { 
        threshold: 0.01, // Ativa assim que o primeiro pixel aparecer
        rootMargin: '100px' // Margem pequena para desmontagem rápida
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const t = {
    pt: { live: 'Obra Viva', static: 'Estático', add: 'Carrinho', nfs: 'Indisponível' },
    en: { live: 'Live Art', static: 'Static', add: 'Add to Cart', nfs: 'Not for Sale' }
  }[language];

  const isVideo = (url?: string) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.includes('.mp4') || 
           lower.includes('.webm') || 
           lower.includes('giphy.com') ||
           lower.includes('blob:') ||
           lower.startsWith('data:video/');
  };

  const hasAnimation = !!item.videoUrl && !videoError;

  return (
    <div 
      ref={cardRef}
      className="art-grid-item group bg-zinc-900/40 border border-white/5 overflow-hidden transition-all duration-300 hover:border-[#c084fc]/50 shadow-lg flex flex-col h-full"
      style={{ transform: 'translateZ(0)' }}
    >
      <a 
        href={`#art/${item.id}/${slugify(item.title[language] || item.title['en'] || '')}`}
        className={`relative block ${getAspectClass(item)} overflow-hidden bg-black cursor-zoom-in`}
        onClick={(e) => {
          if (!e.metaKey && !e.ctrlKey) {
            e.preventDefault();
            onPreview(item);
          }
        }}
      >
        {/* Camada 1: Imagem Estática (Sempre presente se "perto" da tela) */}
        {isNearScreen && (
          <img 
            src={imgError ? MASTER_FALLBACK_IMAGE : (item.imageUrl || MASTER_FALLBACK_IMAGE)} 
            alt={item.title[language]} 
            loading="lazy"
            decoding="async"
            className={`absolute inset-0 w-full h-full object-cover z-0 transition-opacity duration-500 ${isVideoLoaded ? 'opacity-0' : 'opacity-100'}`}
            onError={() => setImgError(true)}
          />
        )}

        {/* Camada 2: VÍDEO (VIRTUALIZADO - Apenas no DOM se visível) */}
        {isExactlyVisible && hasAnimation && (
          <div className="absolute inset-0 z-10 pointer-events-none bg-black">
            {isVideo(item.videoUrl) ? (
              <video 
                src={item.videoUrl}
                autoPlay 
                loop 
                muted 
                playsInline 
                preload="metadata"
                onLoadedData={() => setIsVideoLoaded(true)}
                onError={() => setVideoError(true)}
                className="w-full h-full object-cover block"
              />
            ) : (
              <img 
                src={item.videoUrl} 
                alt="Animated Artwork"
                onLoad={() => setIsVideoLoaded(true)}
                className="w-full h-full object-cover block"
                onError={() => setVideoError(true)}
              />
            )}
          </div>
        )}

        {/* Camada de Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-20 pointer-events-none"></div>
        
        <div className="absolute top-3 left-3 flex flex-col gap-2 z-30">
          <span className={`text-[8px] font-black px-2.5 py-1.5 flex items-center gap-1.5 uppercase tracking-widest shadow-xl ${
            item.isAnimated ? 'bg-[#c084fc] text-white' : 'bg-zinc-800 text-zinc-400'
          }`}>
            {item.isAnimated ? <Zap className="w-2.5 h-2.5 fill-current" /> : <ImageIcon className="w-2.5 h-2.5" />}
            {item.isAnimated ? t.live : t.static}
          </span>
        </div>

        {hasDiscount && (
          <div className="absolute top-3 right-3 z-30 animate-pulse">
            <span className="bg-red-500 text-white text-[8px] font-black px-2.5 py-1.5 uppercase tracking-widest shadow-xl">
              -{discountPercentage}% OFF
            </span>
          </div>
        )}

        <div className="absolute inset-0 z-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200">
           <Maximize2 className="w-6 h-6 text-white" />
        </div>
      </a>

      <div className="p-5 bg-zinc-950/20 flex-1 flex flex-col justify-between">
        <div className="mb-4">
          <h3 className="font-black text-white group-hover:text-[#c084fc] transition-colors text-sm tracking-tight uppercase leading-tight line-clamp-2 min-h-[2.5rem]">
            {item.title[language]}
          </h3>
          <div className="flex justify-between items-center mt-3">
            <span className="text-[7px] text-zinc-600 font-black uppercase tracking-widest flex flex-col gap-0.5">
              <span>{item.category}</span>
              {item.subcategory && <span className="text-zinc-500 text-[6px] font-medium">{item.subcategory}</span>}
            </span>
            {item.notForSale ? (
              <span className="text-xs font-mono font-bold text-zinc-700">
                {t.nfs}
              </span>
            ) : hasDiscount ? (
              <div className="flex flex-col items-end">
                <span className="line-through text-[10px] text-zinc-500 font-mono leading-none mb-1">
                  {language === 'en' ? `$ ${item.price.toFixed(2)}` : `R$ ${(item.price * 5).toFixed(2)}`}
                </span>
                <span className="text-xs font-mono font-black text-[#c084fc] leading-none">
                  {language === 'en' ? `$ ${finalPrice.toFixed(2)}` : `R$ ${(finalPrice * 5).toFixed(2)}`}
                </span>
              </div>
            ) : (
              <span className="text-xs font-mono font-bold text-[#c084fc]">
                {language === 'en' ? `$ ${item.price.toFixed(2)}` : `R$ ${(item.price * 5).toFixed(2)}`}
              </span>
            )}
          </div>

          {/* Individual 5-Star Rating component directly below each art metadata */}
          <div className="mt-4 pt-3 border-t border-white/5 flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-0.5" onMouseLeave={() => setHoverStar(null)}>
                {[1, 2, 3, 4, 5].map((star) => {
                  const isFilled = hoverStar !== null ? star <= hoverStar : star <= Math.round(displayAverage);
                  return (
                    <button
                      key={star}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleRate(star);
                      }}
                      onMouseEnter={() => setHoverStar(star)}
                      className="transition-transform active:scale-125 focus:outline-none p-0.5"
                      title={`${language === 'pt' ? 'Avaliar' : 'Rate'} ${star} ${language === 'pt' ? 'estrelas' : 'stars'}`}
                    >
                      <Star
                        className={`w-3.5 h-3.5 transition-all duration-150 ${
                          isFilled 
                            ? 'text-[#c084fc] fill-[#c084fc] drop-shadow-[0_0_4px_rgba(192,132,252,0.6)]' 
                            : 'text-zinc-800 hover:text-zinc-500'
                        }`}
                      />
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-1.5 select-none">
                <span className="text-[10px] font-bold text-white font-mono">{displayAverage.toFixed(1)}</span>
                <span className="text-[8px] font-medium text-zinc-500 font-mono">({displayCount})</span>
              </div>
            </div>
            {showSavedMsg && (
              <span className="text-[7px] text-emerald-400 font-black uppercase tracking-widest animate-pulse">
                {language === 'pt' ? '✓ Avaliação salva!' : '✓ Rating saved!'}
              </span>
            )}
          </div>
        </div>
        
         {!item.notForSale && (
          <div className="flex flex-col gap-2">
            {!item.hideBuyButton && (
              <button 
                onClick={(e) => { e.stopPropagation(); onAddToCart(item); }}
                className="w-full bg-white hover:bg-[#c084fc] text-black hover:text-white font-black py-3 flex items-center justify-center gap-2 transition-all tracking-widest text-[8px] uppercase active:scale-95"
              >
                <Flower className="w-3.5 h-3.5" />
                {t.add}
              </button>
            )}

            {item.stripeUrlBr && (
              <a 
                href={item.stripeUrlBr}
                target="_blank" 
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="w-full bg-[#c084fc] hover:bg-white text-white hover:text-black font-black py-3 flex items-center justify-center gap-2 transition-all shadow-[0_4px_12px_rgba(192,132,252,0.25)] active:scale-95 border-b-2 border-white/20 uppercase tracking-widest text-[8px] text-center"
              >
                <CreditCard className="w-3.5 h-3.5" />
                {language === 'pt' ? 'Pagar com Stripe (BRL / PIX)' : 'Pay with Stripe (BRL / PIX)'}
              </a>
            )}

            {item.stripeUrl && (
              <a 
                href={item.stripeUrl}
                target="_blank" 
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="w-full bg-zinc-900 border border-zinc-800 hover:border-white text-zinc-300 hover:text-white font-black py-3 flex items-center justify-center gap-2 transition-all shadow-xl active:scale-95 uppercase tracking-widest text-[8px] text-center"
              >
                <CreditCard className="w-3.5 h-3.5" />
                {language === 'pt' ? 'Pagar com Stripe (USD)' : 'Pay with Stripe (USD)'}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtCard;
